<template>
  <a-spin :spinning="!showPage" size="large" tip="检查权限中..." />
  <a-layout class="main-layout" v-if="showPage">
    <a-layout-header class="header-fixed">
      <div class="logo">
        <img src="@/assets/logo.png" alt="OpenHarmony Logo" class="logo-img" />
      </div>
      <div class="title">
        开源鸿蒙精细化运营平台
      </div>
      <a-menu v-model:selectedKeys="selectedKeys" mode="horizontal" class="menu"
        :overflowedIndicator="overflowedIndicator">
        <a-menu-item v-for="item in menuItems" :key="item.key" class="menu-item">
          <span>{{ item.label }}</span>
        </a-menu-item>
      </a-menu>
      <div class="header-user" v-if="user">
        <a-button type="text" class="ai-agent-btn" @click="goToAiAgent">
          <span class="ai-agent-content">
            <robot-outlined class="ai-agent-icon" />
            <span class="ai-agent-text">AI Agent</span>
          </span>
        </a-button>
        <a-tooltip placement="bottom">
          <template #title>
            <div>平台数据月度更新</div>
            <div>上次更新时间为：2026 年 7 月</div>
            <div>有临时更新需求 请联系庄文琪</div>
          </template>
          <info-circle-outlined class="info-icon" />
        </a-tooltip>
        <a-avatar>
          <template #icon>
            <user-outlined />
          </template>
        </a-avatar>
        <div class="user-meta">
          <span class="user-name">{{ user.name || '访客' }}</span>
          <span class="user-email">{{ user.email || '未登录' }}</span>
        </div>
        <a-button v-if="!isGuestUser" type="text" class="logout-btn" @click="handleAuthAction">
          <span class="logout-content">
            <logout-outlined />
            <span class="logout-text">退出</span>
          </span>
        </a-button>
        <a-button v-else type="text" class="login-btn" @click="handleAuthAction">
          <span class="login-content">
            <login-outlined />
            <span class="login-text">登录</span>
          </span>
        </a-button>
      </div>
    </a-layout-header>
    <a-layout class="body-area">
      <component :is="currentContent" />
    </a-layout>
  </a-layout>
</template>

<script setup>
import { computed, h, ref, onMounted } from 'vue';
import axios from 'axios';
import ContributorView from './contributor/ContributorPage.vue';
import SigCommitterPage from './sig-committer/SigCommitterPage.vue';
import openAtomApi from '@/openAtomApi';
import { UserOutlined, LogoutOutlined, LoginOutlined, MoreOutlined, InfoCircleOutlined, RobotOutlined } from '@ant-design/icons-vue';
import Cookies from 'js-cookie';
import PrdDeveloperPage from './product-developer/PrdDeveloperPage.vue';
import SouthboundPage from './southbound/SouthboundPage.vue';
import VodPage from './vod/VodPage.vue';
import SummaryReport from './summary-report/SummaryReport.vue';
const selectedKeys = ref(['0']);

// 当菜单项超出可视区域时，Ant Design 会自动折叠多余项到一个“更多”下拉
// 这里我们使用 Ant Design 预设的 MoreOutlined 图标作为折叠指示器
const overflowedIndicator = h(MoreOutlined);

const menuItems = [
  { key: '0', label: '贡献者', component: ContributorView },
  { key: '1', label: 'SIG&Committer', component: SigCommitterPage },
  { key: '2', label: '南向统一互联', component: SouthboundPage },
  { key: '3', label: '产品开发者', component: PrdDeveloperPage },
  { key: '4', label: 'VOD', component: VodPage },
  { key: '5', label: '报告下载', component: SummaryReport }
];

const user = ref({});
const showPage = ref(false);

const isGuestUser = computed(() => {
  const currentUser = user.value || {};
  return Object.keys(currentUser).length === 0;
});

const currentContent = computed(() => {
  const key = selectedKeys.value[0] ?? '0';
  return menuItems.find((item) => item.key === key)?.component;
});

onMounted(async () => {
  try {
    // 先拉取用户信息，再进行权限校验
    const openAtomRes = await openAtomApi.get('/user/info');
    user.value = openAtomRes.data || {};
    const res = await axios.post('/requestHeader/checkPermissions', null, {
      params: {
        userId: user.value.id,
        opRequestHeaderId: null,
        operationCode: 'LOCK',
      }
    });
    if (res.data.code !== 200) {
      throw new Error(res.data.desc);
    }
    showPage.value = true;
  } catch (err) {
    if (err.response?.status === 403) {
      window.location.href = '/403';
    } else {
      console.error('Failed to fetch user info:', err);
      window.location.href = '/500';
    }
  }
});

const handleAuthAction = () => {
  if (!isGuestUser.value) {
    Cookies.remove('accessToken', { path: '/' });
    Cookies.remove('refreshToken', { path: '/' });
    localStorage.removeItem('token_timestamp');
  }
  window.location.href = '/login';
};

const goToAiAgent = () => {
  window.location.href = '//aiagent.chaobangai.com';
};

</script>
<style scoped>
.main-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  /* 轻微整体背景渐变，保持浅色基调 */
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
}

.header-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.72);
  -webkit-backdrop-filter: blur(8px) saturate(120%);
  backdrop-filter: blur(8px) saturate(120%);
  display: flex;
  padding: 0 24px;
  box-shadow: 0 6px 20px rgba(20, 37, 67, 0.06);
}

.logo {
  height: 40px;
  margin: 0 24px 0 0;
}

.logo-img {
  width: 100%;
  height: 100%;
}

.title {
  font-size: 20px;
  margin-right: 50px;
  line-height: 64px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.menu {
  flex: 1;
  min-width: 0;
  /* allow flex item to shrink and trigger Menu overflow handling */
}

.header-user {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 24px;
}

.ai-agent-btn {
  color: #0f437f;
  padding: 0 8px;
  background: rgba(80, 162, 255, 0.08);
  border-radius: 6px;
  border: 1px solid rgba(80, 162, 255, 0.2);
}

.ai-agent-btn:hover {
  color: #50A2FF;
  background: rgba(80, 162, 255, 0.15);
  border-color: rgba(80, 162, 255, 0.4);
}

.ai-agent-content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ai-agent-icon {
  font-size: 16px;
}

.ai-agent-text {
  font-size: 13px;
  font-weight: 500;
}

.logout-btn {
  color: #0f437f;
  padding: 0 4px;
}

.logout-btn:hover {
  color: #d14343;
}

.login-btn {
  color: #2b67a8;
  padding: 0 6px;
  background: rgba(43, 103, 168, 0.08);
  border-radius: 6px;
}

.login-btn:hover {
  color: #1f4f86;
  background: rgba(43, 103, 168, 0.16);
}

.info-icon {
  font-size: 18px;
  color: #f5b619;
}

.logout-content {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.logout-text {
  padding: 2px;
}

.login-content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.login-text {
  padding: 2px;
}

.user-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  text-align: left;
}

.user-name {
  font-size: 14px;
  color: #0f437f;
  font-weight: 500;
}

.user-email {
  font-size: 12px;
  color: #6b7280;
}

/* 菜单项使用较深的蓝色，保持可读性 */
.menu-item {
  font-size: 16px;
  color: #0f437f;
}

.body-area {
  display: flex;
  flex: 1;
  margin-top: 64px;
  min-height: 0;
  height: calc(100vh - 64px);
  overflow: hidden;
}

/* coming-soon 文本使用深蓝色，视觉和整体配色一致 */
.coming-soon {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #2b67a8;
}
</style>